import { Fragment, PropsWithChildren } from 'react';
import { useMutation } from 'react-query';
import { Alert, Button, Space, Spin } from 'antd';
import { Logout } from 'api/utils';

import { useLogAccessProfile } from './useLogAccessProfile';
import { useLogAccessSession } from './useLogAccessSession';

import styles from './ScopedLogs.module.scss';

function LogAccessBoundary({ children }: PropsWithChildren): JSX.Element {
	const session = useLogAccessSession();
	const profile = useLogAccessProfile(session.id, session.authenticated);
	const logout = useMutation(Logout);

	if (!session.authenticated) {
		return <>{children}</>;
	}
	if (profile.isLoading) {
		return (
			<main className={styles.status} data-testid="log-access-loading">
				<Spin tip="Checking access…" />
			</main>
		);
	}
	if (profile.isError || !profile.data) {
		return (
			<main className={styles.status} data-testid="log-access-error">
				<Alert
					type="error"
					message="Unable to verify your access"
					description="Your account access could not be checked. Retry or sign out."
				/>
				<Space>
					<Button
						data-testid="log-access-retry"
						onClick={(): void => {
							void profile.refetch();
						}}
					>
						Retry
					</Button>
					<Button
						data-testid="log-access-sign-out"
						loading={logout.isLoading}
						onClick={(): void => logout.mutate()}
					>
						Sign out
					</Button>
				</Space>
			</main>
		);
	}
	return (
		<Fragment key={`${session.id}:${JSON.stringify(profile.data)}`}>
			{children}
		</Fragment>
	);
}

export default LogAccessBoundary;
